import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDesktop,
  faLaptop,
  faMobileScreenButton,
  faFolder,
  faFolderPlus,
  faFile,
  faChevronLeft,
  faUpload,
  faGlobe,
  faLayerGroup,
  faClockRotateLeft,
  faMagnifyingGlass,
  faXmark,
  faSort,
  faSortUp,
  faSortDown,
  faFileZipper,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { TopNav } from '../shared/components/navigation/TopNav';
import { PaperContainer } from '../shared/components/containers/PaperContainer';
import { PrimaryButton } from '../shared/components/buttons/PrimaryButton';
import {
  Device,
  ObjectListing,
  FileVersion,
  SearchResults,
  listDevices,
  deleteDevice,
  getOrCreateWebFolder,
  listObjects,
  listObjectsAsOf,
  searchObjects,
  createFolder,
  presignDownload,
  listFileVersions,
  deleteFile,
  deleteFolder,
} from '../api/backupApi';
import { ApiError } from '../api/client';
import { EncryptionGate } from '../Backup/EncryptionGate';
import { uploadFile } from '../Backup/uploadFile';
import { downloadFolderZip, FolderTooLargeError } from '../Backup/downloadFolderZip';
import { useEncryption } from '../context/EncryptionContext';
import { decryptObjectBytes, unwrapKey } from '../crypto/backupCrypto';

// Matches react-bootstrap/Bootstrap's own "md" breakpoint (see TopNav's
// `expand="md"`) so the nav collapses and the listing table switches to
// its compact mobile layout at the same width, rather than at two
// different, unrelated points.
const MOBILE_BREAKPOINT = 768;

const PageWrapper = styled.div`
  padding: 20px;
`;

const PageHeader = styled.h1`
  font-size: 1.4em;
  margin-bottom: 15px;
`;

const SectionHeader = styled.h2`
  font-size: 1em;
  color: ${(props) => props.theme.colors.mutedText};
  margin: 20px 0 10px;

  &:first-child {
    margin-top: 0;
  }
`;

const DeviceGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const DeviceCard = styled(PaperContainer)`
  width: 220px;
  padding: 20px;
  cursor: pointer;

  &:hover {
    box-shadow: 5px 5px 5px darkgray;
  }
`;

const DeviceIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
  color: ${(props) => props.theme.colors.primary};
`;

const DeviceCardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const DeviceName = styled.span`
  font-weight: 600;
`;

const DevicePlatform = styled.div`
  margin-top: 5px;
  color: ${(props) => props.theme.colors.mutedText};
  font-size: 0.85em;
  text-transform: capitalize;
`;

const DeviceSubtitle = styled.div`
  margin-top: 5px;
  color: ${(props) => props.theme.colors.mutedText};
  font-size: 0.85em;
`;

const Breadcrumb = styled.div`
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const BreadcrumbTrail = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const CrumbLink = styled.span`
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const BackCrumbLink = styled(CrumbLink)`
  margin-right: 8px;
`;

const CrumbSeparator = styled.span`
  margin: 0 6px;
  color: ${(props) => props.theme.colors.mutedText};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const NewFolderForm = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NewFolderInput = styled.input`
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 3px;
  padding: 6px 10px;
`;

const ListingContainer = styled(PaperContainer)`
  padding: 10px 20px;
  // Safety net, not the primary mobile fix (see the hidden-column media
  // queries below) — contains any residual overflow to the listing itself
  // rather than letting it widen the whole page (which is what happens by
  // default: a flex child wider than its container pushes the page's own
  // scrollable width out with it).
  overflow-x: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const RowIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
  color: ${(props) => props.theme.colors.mutedText};
  width: 18px;
`;

const RowName = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowSubtitle = styled.div`
  color: ${(props) => props.theme.colors.mutedText};
  font-size: 0.75em;
`;

// Below MOBILE_BREAKPOINT, the Modified/Uploaded/Type columns are hidden
// entirely (see those cells' own media queries) rather than left to
// squeeze into a viewport too narrow for 5 fixed-width columns — this
// shows a compact summary of what's hidden instead, same idea as
// RowSubtitle already does for a search result's parent path.
const RowSubtitleMobile = styled(RowSubtitle)`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;

const RowMeta = styled.span`
  color: ${(props) => props.theme.colors.mutedText};
  font-size: 0.85em;
`;

// Hidden below MOBILE_BREAKPOINT — see RowSubtitleMobile, which shows a
// compact summary of this same info under the file's name instead. Size
// stays visible always: it's the one column narrow enough, and important
// enough, to keep even on a phone.
const HiddenOnMobile = `
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const TypeCell = styled(RowMeta)`
  width: 90px;
  flex-shrink: 0;
  text-align: right;
  ${HiddenOnMobile}
`;

const ModifiedCell = styled(RowMeta)`
  width: 140px;
  flex-shrink: 0;
  text-align: right;
  ${HiddenOnMobile}
`;

const LocalModifiedCell = styled(RowMeta)`
  width: 140px;
  flex-shrink: 0;
  text-align: right;
  ${HiddenOnMobile}
`;

const SizeCell = styled(RowMeta)`
  width: 160px;
  flex-shrink: 0;
  text-align: right;
`;

const ListHeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0 8px;
  border-bottom: 2px solid ${(props) => props.theme.colors.border};
  font-size: 0.8em;
  font-weight: 600;
  color: ${(props) => props.theme.colors.mutedText};
`;

const ListHeaderSpacer = styled.span`
  width: 28px;
  flex-shrink: 0;
`;

const SortableHeader = styled.span`
  cursor: pointer;
  user-select: none;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const NameHeader = styled(SortableHeader)`
  flex: 1;
`;

const TypeHeader = styled(SortableHeader)`
  width: 90px;
  flex-shrink: 0;
  text-align: right;
  ${HiddenOnMobile}
`;

const ModifiedHeader = styled(SortableHeader)`
  width: 140px;
  flex-shrink: 0;
  text-align: right;
  ${HiddenOnMobile}
`;

const LocalModifiedHeader = styled(SortableHeader)`
  width: 140px;
  flex-shrink: 0;
  text-align: right;
  ${HiddenOnMobile}
`;

const SizeHeader = styled(SortableHeader)`
  width: 160px;
  flex-shrink: 0;
  text-align: right;
`;

const SortIcon = styled(FontAwesomeIcon)`
  margin-left: 4px;
`;

const RowActionButton = styled(FontAwesomeIcon)`
  margin-left: 12px;
  color: ${(props) => props.theme.colors.mutedText};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const DeleteRowActionButton = styled(RowActionButton)`
  &:hover {
    color: ${(props) => props.theme.colors.error};
  }
`;

const DangerButton = styled(PrimaryButton)`
  background-color: ${(props) => props.theme.colors.error};
  border-color: ${(props) => props.theme.colors.error};

  &:hover {
    opacity: 0.9;
  }
`;

const ModalFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
`;

const VersionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const VersionBadge = styled.span`
  margin-left: 8px;
  font-size: 0.75em;
  color: ${(props) => props.theme.colors.mutedText};
`;

const TimeMachineToggle = styled(CrumbLink)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeMachineBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const TimeMachineBanner = styled.div`
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 3px;
  padding: 8px 14px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 160px;
`;

const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: 10px;
  color: ${(props) => props.theme.colors.mutedText};
  pointer-events: none;
`;

const SearchInput = styled(NewFolderInput)`
  padding-left: 30px;
  padding-right: 28px;
  width: 100%;
  max-width: 260px;
`;

const ClearSearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 10px;
  color: ${(props) => props.theme.colors.mutedText};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const SearchStatus = styled.span`
  color: ${(props) => props.theme.colors.mutedText};
  font-size: 0.85em;
`;

const EmptyState = styled.div`
  padding: 20px 0;
  color: ${(props) => props.theme.colors.mutedText};
`;

const ErrorText = styled.div`
  color: ${(props) => props.theme.colors.error};
`;

const platformIcon = (platform: string) => {
  switch (platform) {
    case 'windows':
      return faDesktop;
    case 'macos':
      return faLaptop;
    default:
      return faMobileScreenButton;
  }
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Search results can come from anywhere under the current folder, so the
// basename alone is ambiguous — show where each match actually lives.
const formatParentPath = (path: string): string => {
  const trimmed = path.replace(/\/$/, '');
  const lastSlash = trimmed.lastIndexOf('/');
  return lastSlash === -1 ? '/' : trimmed.slice(0, lastSlash + 1);
};

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString();

// Neither backend response includes a content-type for listing entries
// (uploads are opaque encrypted blobs server-side, see Storage::
// EncryptionMetadata) — "Type" is derived purely from the filename's own
// extension, client-side. `lastDot <= 0` also excludes dotfiles like
// ".gitignore", where the leading dot isn't an extension separator.
const fileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.slice(lastDot + 1).toUpperCase() : '';
};

const MIN_SEARCH_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

type SortColumn = 'name' | 'type' | 'modified' | 'localModified' | 'size';
type SortDirection = 'asc' | 'desc';

// `size`/`last_modified` are optional on the folder side — neither the
// normal listing nor search results carry a folder's own modified date
// (folders aren't real objects), and search results specifically don't
// carry a folder size either (the search endpoint doesn't compute one, see
// backupApi.ts). `local_modified_at` is *never* present for folders (it's
// purely a per-file, cached-at-upload-time value, see backupApi.ts) — same
// "leave in relative order" treatment. Sorting by a column a given item
// doesn't have just leaves it in its original relative order rather than
// throwing or faking a value. "Type" is skipped entirely for folders —
// every folder's type is the same ("Folder"), so there's nothing to
// differentiate within the group anyway.
const sortFolders = <T extends { name: string; size?: number; last_modified?: string; local_modified_at?: string | null }>(
  folders: T[], column: SortColumn, direction: SortDirection
): T[] => {
  if (column === 'type') return folders;
  const sorted = [...folders];
  sorted.sort((a, b) => {
    let cmp: number;
    if (column === 'name') {
      cmp = a.name.localeCompare(b.name);
    } else if (column === 'size') {
      cmp = (a.size ?? 0) - (b.size ?? 0);
    } else if (column === 'localModified') {
      cmp = (a.local_modified_at ?? '').localeCompare(b.local_modified_at ?? '');
    } else {
      cmp = (a.last_modified ?? '').localeCompare(b.last_modified ?? '');
    }
    return direction === 'asc' ? cmp : -cmp;
  });
  return sorted;
};

const sortFiles = <T extends { path: string; size: number; last_modified: string; local_modified_at?: string | null }>(
  files: T[], column: SortColumn, direction: SortDirection
): T[] => {
  const sorted = [...files];
  sorted.sort((a, b) => {
    let cmp: number;
    if (column === 'name') {
      cmp = (a.path.split('/').pop() ?? '').localeCompare(b.path.split('/').pop() ?? '');
    } else if (column === 'type') {
      cmp = fileExtension(a.path).localeCompare(fileExtension(b.path));
    } else if (column === 'size') {
      cmp = a.size - b.size;
    } else if (column === 'localModified') {
      cmp = (a.local_modified_at ?? '').localeCompare(b.local_modified_at ?? '');
    } else {
      cmp = a.last_modified.localeCompare(b.last_modified);
    }
    return direction === 'asc' ? cmp : -cmp;
  });
  return sorted;
};

// `datetime-local` inputs read/write in the *local* wall-clock timezone —
// `Date#toISOString()` is always UTC, so using it directly as `max` clamps
// "now" to hours in the past/future for anyone outside UTC. Shift by the
// timezone offset first so the printed digits match local wall-clock time.
const toLocalDatetimeInputValue = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const MainPage = () => {
  const { t } = useTranslation();
  const { masterKey } = useEncryption();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [prefix, setPrefix] = useState<string>('');
  const [listing, setListing] = useState<ObjectListing | null>(null);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [submittingNewFolder, setSubmittingNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [openingWebFolder, setOpeningWebFolder] = useState(false);
  const [timeMachineAt, setTimeMachineAt] = useState<Date | null>(null);
  const [showTimeMachinePicker, setShowTimeMachinePicker] = useState(false);
  const [versionsFile, setVersionsFile] = useState<string | null>(null);
  const [versions, setVersions] = useState<FileVersion[] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [zipping, setZipping] = useState<{ completed: number; total: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'file' | 'folder' | 'device'; path: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortIconFor = (column: SortColumn) => {
    if (sortColumn !== column) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  useEffect(() => {
    listDevices()
      .then(setDevices)
      .catch((e) => setError(e instanceof ApiError ? e.message : t('mainPage.errors.failedToLoadDevices')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshListing = () => {
    if (!selectedDevice) {
      return;
    }
    setListing(null);
    const promise = timeMachineAt
      ? listObjectsAsOf(selectedDevice.uuid, prefix, timeMachineAt)
      : listObjects(selectedDevice.uuid, prefix);
    promise
      .then(setListing)
      .catch((e) => setError(e instanceof ApiError ? e.message : t('mainPage.errors.failedToLoadFiles')));
  };

  useEffect(refreshListing, [selectedDevice, prefix, timeMachineAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search is always scoped to "wherever you currently are" — start fresh
  // every time that changes, rather than carrying a stale query/scope into
  // a folder the user just navigated into (including via clicking a search
  // result itself, which is what exits search mode).
  useEffect(() => {
    setSearchQuery('');
    setSearchResults(null);
  }, [selectedDevice, prefix]);

  useEffect(() => {
    if (!selectedDevice || timeMachineAt) {
      return;
    }
    const trimmed = searchQuery.trim();
    if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) {
      setSearchResults(null);
      return;
    }

    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      searchObjects(selectedDevice.uuid, prefix, trimmed)
        .then((res) => {
          if (!cancelled) setSearchResults(res);
        })
        .catch((e) => {
          if (!cancelled) setError(e instanceof ApiError ? e.message : t('mainPage.errors.searchFailed'));
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedDevice, prefix, timeMachineAt]);

  const openDevice = (device: Device) => {
    setError(null);
    setSelectedDevice(device);
    setPrefix('');
    setTimeMachineAt(null);
    setShowTimeMachinePicker(false);
  };

  const openWebFolder = async () => {
    setError(null);
    setOpeningWebFolder(true);
    try {
      const webFolder = await getOrCreateWebFolder();
      openDevice(webFolder);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('mainPage.errors.failedToOpenWebFolder'));
    } finally {
      setOpeningWebFolder(false);
    }
  };

  const backToDevices = () => {
    setSelectedDevice(null);
    setListing(null);
    setPrefix('');
    setTimeMachineAt(null);
    setShowTimeMachinePicker(false);
  };

  const onTimeMachineDateChosen = (value: string) => {
    if (!value) {
      return;
    }
    setTimeMachineAt(new Date(value));
  };

  const exitTimeMachine = () => {
    setTimeMachineAt(null);
    setShowTimeMachinePicker(false);
  };

  const openVersions = (path: string) => {
    if (!selectedDevice) {
      return;
    }
    setError(null);
    setVersionsFile(path);
    setVersions(null);
    listFileVersions(selectedDevice.uuid, path)
      .then((res) => setVersions(res.versions))
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : t('mainPage.errors.failedToLoadVersions'));
        setVersionsFile(null);
      });
  };

  const closeVersions = () => {
    setVersionsFile(null);
    setVersions(null);
  };

  const submitNewFolder = async () => {
    const sanitized = newFolderName.trim();
    if (!sanitized || sanitized.includes('/')) {
      setError(t('mainPage.errors.folderNameInvalid'));
      return;
    }
    if (!selectedDevice) {
      return;
    }
    setError(null);
    setSubmittingNewFolder(true);
    try {
      // Folders are otherwise purely virtual (S3 has no real directories,
      // only object key prefixes) — this PUTs a real zero-byte marker
      // object so the folder persists/shows up in listings even while
      // still empty, rather than only "existing" once something else is
      // uploaded into it.
      const newPrefix = `${prefix}${sanitized}/`;
      await createFolder(selectedDevice.uuid, newPrefix);
      setPrefix(newPrefix);
      setCreatingFolder(false);
      setNewFolderName('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('mainPage.errors.failedToCreateFolder'));
    } finally {
      setSubmittingNewFolder(false);
    }
  };

  const cancelNewFolder = () => {
    setCreatingFolder(false);
    setNewFolderName('');
  };

  const openFile = async (path: string, versionId?: string) => {
    if (!selectedDevice || !masterKey) {
      return;
    }
    setError(null);
    setOpeningPath(path);
    try {
      const { url, encryption } = await presignDownload(selectedDevice.uuid, path, versionId);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`download failed: HTTP ${response.status}`);
      }
      const ciphertext = await response.arrayBuffer();

      const dek = await unwrapKey(
        { wrapped: encryption.wrapped_dek, nonce: encryption.dek_nonce },
        masterKey
      );
      const plaintext = await decryptObjectBytes(
        ciphertext,
        dek,
        encryption.chunk_nonce_prefix,
        parseInt(encryption.chunk_size, 10)
      );

      // File.type (what we stored as original_content_type) never includes
      // a charset — without one, browsers guess Latin-1 for text/* blobs,
      // mangling anything outside ASCII. We only ever produce UTF-8 text,
      // so make that explicit rather than relying on the browser's guess.
      const contentType = encryption.original_content_type.startsWith('text/')
        ? `${encryption.original_content_type}; charset=utf-8`
        : encryption.original_content_type;
      const blob = new Blob([plaintext], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      // Give the new tab time to actually load the blob before freeing it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('mainPage.errors.failedToOpenFile'));
    } finally {
      setOpeningPath(null);
    }
  };

  // `folderPrefix` is the folder's real path (relative to the device root);
  // `displayName` is just for the zip's filename — no trailing slash.
  const downloadFolder = async (folderPrefix: string, displayName: string) => {
    if (!selectedDevice || !masterKey || zipping) {
      return;
    }
    setError(null);
    setZipping({ completed: 0, total: 0 });
    try {
      await downloadFolderZip(selectedDevice.uuid, folderPrefix, masterKey, `${displayName}.zip`, {
        onProgress: (completed, total) => setZipping({ completed, total }),
      });
    } catch (e) {
      setError(e instanceof FolderTooLargeError ? t('mainPage.errors.folderTooLargeToZip') : t('mainPage.errors.failedToDownloadZip'));
    } finally {
      setZipping(null);
    }
  };

  const requestDeleteFile = (path: string, name: string) => setConfirmDelete({ type: 'file', path, name });
  const requestDeleteFolder = (prefix: string, name: string) => setConfirmDelete({ type: 'folder', path: prefix, name });
  // `path` carries the device uuid here — same reuse-the-field trick as
  // 'folder' storing its prefix in `path`.
  const requestDeleteDevice = (uuid: string, name: string) => setConfirmDelete({ type: 'device', path: uuid, name });
  const cancelDelete = () => setConfirmDelete(null);

  const confirmDeleteAction = async () => {
    if (!confirmDelete) {
      return;
    }
    if (confirmDelete.type !== 'device' && !selectedDevice) {
      return;
    }
    setError(null);
    setDeleting(true);
    try {
      if (confirmDelete.type === 'file') {
        await deleteFile(selectedDevice!.uuid, confirmDelete.path);
      } else if (confirmDelete.type === 'folder') {
        await deleteFolder(selectedDevice!.uuid, confirmDelete.path);
      } else {
        await deleteDevice(confirmDelete.path);
        setDevices((current) => current?.filter((d) => d.uuid !== confirmDelete.path) ?? current);
      }
      setConfirmDelete(null);
      if (confirmDelete.type !== 'device') {
        refreshListing();
      }
    } catch (e) {
      setError(
        confirmDelete.type === 'file'
          ? t('mainPage.errors.failedToDeleteFile')
          : confirmDelete.type === 'folder'
            ? t('mainPage.errors.failedToDeleteFolder')
            : t('mainPage.errors.failedToDeleteDevice')
      );
    } finally {
      setDeleting(false);
    }
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file next time
    if (!file || !selectedDevice || !masterKey) {
      return;
    }

    setError(null);
    setUploading(true);
    try {
      await uploadFile(file, `${prefix}${file.name}`, selectedDevice.uuid, masterKey);
      refreshListing();
    } catch (e) {
      setError(t('mainPage.errors.failedToUploadFile'));
    } finally {
      setUploading(false);
    }
  };

  const crumbs = prefix.split('/').filter(Boolean);
  const currentFolderDisplayName = crumbs.length > 0 ? crumbs[crumbs.length - 1] : selectedDevice?.name ?? 'download';

  return (
    <div>
      <TopNav/>
      <PageWrapper>
        <PageHeader>{t('mainPage.title')}</PageHeader>

        <EncryptionGate>
          {error && <ErrorText>{error}</ErrorText>}

          {!selectedDevice && (
            <>
              <SectionHeader>{t('mainPage.webFolder.sectionHeader')}</SectionHeader>
              <DeviceGrid>
                <DeviceCard onClick={openWebFolder}>
                  <div>
                    <DeviceIcon icon={faGlobe}/>
                    <DeviceName>{openingWebFolder ? t('mainPage.webFolder.opening') : t('mainPage.webFolder.name')}</DeviceName>
                  </div>
                  <DeviceSubtitle>{t('mainPage.webFolder.subtitle')}</DeviceSubtitle>
                </DeviceCard>
              </DeviceGrid>

              <SectionHeader>{t('mainPage.devices.sectionHeader')}</SectionHeader>
              <DeviceGrid>
                {devices === null && !error && <EmptyState>{t('mainPage.devices.loading')}</EmptyState>}
                {devices?.length === 0 && <EmptyState>{t('mainPage.devices.empty')}</EmptyState>}
                {devices?.map((device) => (
                  <DeviceCard key={device.uuid} onClick={() => openDevice(device)}>
                    <DeviceCardTopRow>
                      <div>
                        <DeviceIcon icon={platformIcon(device.platform)}/>
                        <DeviceName>{device.name}</DeviceName>
                      </div>
                      <DeleteRowActionButton
                        icon={faTrash}
                        title={t('mainPage.delete.deviceTooltip')}
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteDevice(device.uuid, device.name);
                        }}
                      />
                    </DeviceCardTopRow>
                    <DevicePlatform>{device.platform}</DevicePlatform>
                  </DeviceCard>
                ))}
              </DeviceGrid>
            </>
          )}

          {selectedDevice && (
            <>
              {timeMachineAt && (
                <TimeMachineBanner>
                  <span>
                    <FontAwesomeIcon icon={faClockRotateLeft}/> {t('mainPage.timeMachine.viewingAsOf', { date: timeMachineAt.toLocaleString() })}
                  </span>
                  <CrumbLink onClick={exitTimeMachine}>{t('mainPage.timeMachine.backToToday')}</CrumbLink>
                </TimeMachineBanner>
              )}

              {!timeMachineAt && (
                <SearchBar>
                  <SearchInputWrapper>
                    <SearchIcon icon={faMagnifyingGlass}/>
                    <SearchInput
                      type="text"
                      placeholder={t('mainPage.search.placeholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <ClearSearchIcon icon={faXmark} onClick={() => setSearchQuery('')}/>
                    )}
                  </SearchInputWrapper>
                  {searching && <SearchStatus>{t('mainPage.search.searching')}</SearchStatus>}
                  {!searching && searchResults?.truncated && (
                    <SearchStatus>
                      {t('mainPage.search.truncated', { count: searchResults.folders.length + searchResults.files.length })}
                    </SearchStatus>
                  )}
                </SearchBar>
              )}

              {!timeMachineAt && (
                <TimeMachineBar>
                  <TimeMachineToggle onClick={() => setShowTimeMachinePicker((v) => !v)}>
                    <FontAwesomeIcon icon={faClockRotateLeft}/> {t('mainPage.timeMachine.toggle')}
                  </TimeMachineToggle>
                  {showTimeMachinePicker && (
                    <NewFolderInput
                      type="datetime-local"
                      max={toLocalDatetimeInputValue(new Date())}
                      onChange={(e) => onTimeMachineDateChosen(e.target.value)}
                    />
                  )}
                </TimeMachineBar>
              )}

              <Breadcrumb>
                <BreadcrumbTrail>
                  <BackCrumbLink onClick={backToDevices}>
                    <FontAwesomeIcon icon={faChevronLeft}/> {selectedDevice.name}
                  </BackCrumbLink>
                  {crumbs.map((segment, idx) => {
                    const segmentPrefix = `${crumbs.slice(0, idx + 1).join('/')}/`;
                    return (
                      <React.Fragment key={segmentPrefix}>
                        <CrumbSeparator>/</CrumbSeparator>
                        <CrumbLink onClick={() => setPrefix(segmentPrefix)}>{segment}</CrumbLink>
                      </React.Fragment>
                    );
                  })}
                </BreadcrumbTrail>

                {/* New folder / Upload only make sense in the web folder while
                    viewing live — real devices are backed up automatically by
                    the native app, a Time Machine view is a read-only
                    snapshot of the past, and while searching the current
                    prefix isn't what's on screen, so none of those allow
                    mutation. Downloading is read-only though, so it's
                    available for every platform whenever there's an
                    unambiguous "current folder" to zip. */}
                {!timeMachineAt && searchResults === null && (
                  creatingFolder ? (
                    <NewFolderForm>
                      <NewFolderInput
                        autoFocus
                        value={newFolderName}
                        disabled={submittingNewFolder}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitNewFolder();
                          if (e.key === 'Escape') cancelNewFolder();
                        }}
                        placeholder={t('mainPage.newFolder.placeholder')}
                      />
                      <PrimaryButton onClick={submitNewFolder} disabled={submittingNewFolder}>
                        {submittingNewFolder ? t('mainPage.newFolder.creating') : t('mainPage.newFolder.create')}
                      </PrimaryButton>
                      <CrumbLink onClick={cancelNewFolder}>{t('mainPage.newFolder.cancel')}</CrumbLink>
                    </NewFolderForm>
                  ) : (
                    <ActionButtons>
                      {selectedDevice.platform === 'web' && (
                        <>
                          <PrimaryButton onClick={() => setCreatingFolder(true)}>
                            <FontAwesomeIcon icon={faFolderPlus}/> {t('mainPage.newFolder.button')}
                          </PrimaryButton>
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={onFileSelected}
                            style={{ display: 'none' }}
                          />
                          <PrimaryButton onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                            <FontAwesomeIcon icon={faUpload}/> {uploading ? t('mainPage.upload.uploading') : t('mainPage.upload.button')}
                          </PrimaryButton>
                        </>
                      )}
                      <PrimaryButton
                        onClick={() => downloadFolder(prefix, currentFolderDisplayName)}
                        disabled={!!zipping}
                      >
                        <FontAwesomeIcon icon={faFileZipper}/> {zipping ? t('mainPage.download.zipping', zipping) : t('mainPage.download.button')}
                      </PrimaryButton>
                    </ActionButtons>
                  )
                )}
              </Breadcrumb>

              <ListingContainer>
                <ListHeaderRow>
                  <ListHeaderSpacer/>
                  <NameHeader onClick={() => toggleSort('name')}>
                    {t('mainPage.columns.name')}
                    <SortIcon icon={sortIconFor('name')}/>
                  </NameHeader>
                  <LocalModifiedHeader onClick={() => toggleSort('localModified')}>
                    {t('mainPage.columns.localModified')}
                    <SortIcon icon={sortIconFor('localModified')}/>
                  </LocalModifiedHeader>
                  <ModifiedHeader onClick={() => toggleSort('modified')}>
                    {t('mainPage.columns.modified')}
                    <SortIcon icon={sortIconFor('modified')}/>
                  </ModifiedHeader>
                  <TypeHeader onClick={() => toggleSort('type')}>
                    {t('mainPage.columns.type')}
                    <SortIcon icon={sortIconFor('type')}/>
                  </TypeHeader>
                  <SizeHeader onClick={() => toggleSort('size')}>
                    {t('mainPage.columns.size')}
                    <SortIcon icon={sortIconFor('size')}/>
                  </SizeHeader>
                </ListHeaderRow>

                {searchResults !== null ? (
                  <>
                    {searchResults.folders.length === 0 && searchResults.files.length === 0 && (
                      <EmptyState>{t('mainPage.search.noMatches')}</EmptyState>
                    )}
                    {sortFolders(searchResults.folders, sortColumn, sortDirection).map((folder) => (
                      <Row key={folder.prefix} onClick={() => setPrefix(folder.prefix)}>
                        <RowIcon icon={faFolder}/>
                        <RowName>
                          {folder.name}
                          <RowSubtitle>{formatParentPath(folder.prefix)}</RowSubtitle>
                        </RowName>
                        <LocalModifiedCell>—</LocalModifiedCell>
                        <ModifiedCell>—</ModifiedCell>
                        <TypeCell>{t('mainPage.columns.typeFolder')}</TypeCell>
                        <SizeCell>—</SizeCell>
                        <RowActionButton
                          icon={faFileZipper}
                          title={t('mainPage.download.tooltip')}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadFolder(folder.prefix, folder.name.replace(/\/$/, ''));
                          }}
                        />
                        {selectedDevice.platform === 'web' && (
                          <DeleteRowActionButton
                            icon={faTrash}
                            title={t('mainPage.delete.folderTooltip')}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteFolder(folder.prefix, folder.name.replace(/\/$/, ''));
                            }}
                          />
                        )}
                      </Row>
                    ))}
                    {sortFiles(searchResults.files, sortColumn, sortDirection).map((file) => (
                      <Row key={file.path} onClick={() => openFile(file.path)}>
                        <RowIcon icon={faFile}/>
                        <RowName>
                          {file.path.split('/').pop()}
                          <RowSubtitle>{formatParentPath(file.path)}</RowSubtitle>
                        </RowName>
                        <LocalModifiedCell>{file.local_modified_at ? formatDate(file.local_modified_at) : '—'}</LocalModifiedCell>
                        <ModifiedCell>{formatDate(file.last_modified)}</ModifiedCell>
                        <TypeCell title={file.content_type ?? undefined}>{fileExtension(file.path) || t('mainPage.columns.typeUnknownFile')}</TypeCell>
                        <SizeCell>{openingPath === file.path ? t('mainPage.listing.opening') : formatSize(file.size)}</SizeCell>
                        <RowActionButton
                          icon={faLayerGroup}
                          title={t('mainPage.versionHistory.tooltip')}
                          onClick={(e) => {
                            e.stopPropagation();
                            openVersions(file.path);
                          }}
                        />
                        {selectedDevice.platform === 'web' && (
                          <DeleteRowActionButton
                            icon={faTrash}
                            title={t('mainPage.delete.fileTooltip')}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteFile(file.path, file.path.split('/').pop() ?? file.path);
                            }}
                          />
                        )}
                      </Row>
                    ))}
                  </>
                ) : (
                  <>
                    {listing === null && <EmptyState>{t('common.loading')}</EmptyState>}
                    {listing && listing.folders.length === 0 && listing.files.length === 0 && (
                      <EmptyState>{t('mainPage.listing.empty')}</EmptyState>
                    )}
                    {listing && sortFolders(listing.folders, sortColumn, sortDirection).map((folder) => (
                      <Row key={folder.prefix} onClick={() => setPrefix(folder.prefix)}>
                        <RowIcon icon={faFolder}/>
                        <RowName>{folder.name}</RowName>
                        <LocalModifiedCell>—</LocalModifiedCell>
                        <ModifiedCell>—</ModifiedCell>
                        <TypeCell>{t('mainPage.columns.typeFolder')}</TypeCell>
                        <SizeCell>{t('mainPage.fileCount', { count: folder.file_count })} · {formatSize(folder.size)}</SizeCell>
                        {!timeMachineAt && (
                          <RowActionButton
                            icon={faFileZipper}
                            title={t('mainPage.download.tooltip')}
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFolder(folder.prefix, folder.name.replace(/\/$/, ''));
                            }}
                          />
                        )}
                        {!timeMachineAt && selectedDevice.platform === 'web' && (
                          <DeleteRowActionButton
                            icon={faTrash}
                            title={t('mainPage.delete.folderTooltip')}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteFolder(folder.prefix, folder.name.replace(/\/$/, ''));
                            }}
                          />
                        )}
                      </Row>
                    ))}
                    {listing && sortFiles(listing.files, sortColumn, sortDirection).map((file) => (
                      <Row key={file.path} onClick={() => openFile(file.path, file.version_id)}>
                        <RowIcon icon={faFile}/>
                        <RowName>
                          {file.path.split('/').pop()}
                          <RowSubtitleMobile>
                            {fileExtension(file.path) || t('mainPage.columns.typeUnknownFile')} · {formatDate(file.local_modified_at ?? file.last_modified)}
                          </RowSubtitleMobile>
                        </RowName>
                        <LocalModifiedCell>{file.local_modified_at ? formatDate(file.local_modified_at) : '—'}</LocalModifiedCell>
                        <ModifiedCell>{formatDate(file.last_modified)}</ModifiedCell>
                        <TypeCell title={file.content_type ?? undefined}>{fileExtension(file.path) || t('mainPage.columns.typeUnknownFile')}</TypeCell>
                        <SizeCell>{openingPath === file.path ? t('mainPage.listing.opening') : formatSize(file.size)}</SizeCell>
                        {!timeMachineAt && (
                          <RowActionButton
                            icon={faLayerGroup}
                            title={t('mainPage.versionHistory.tooltip')}
                            onClick={(e) => {
                              e.stopPropagation();
                              openVersions(file.path);
                            }}
                          />
                        )}
                        {!timeMachineAt && selectedDevice.platform === 'web' && (
                          <DeleteRowActionButton
                            icon={faTrash}
                            title={t('mainPage.delete.fileTooltip')}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteFile(file.path, file.path.split('/').pop() ?? file.path);
                            }}
                          />
                        )}
                      </Row>
                    ))}
                  </>
                )}
              </ListingContainer>
            </>
          )}
        </EncryptionGate>

        <Modal show={versionsFile !== null} onHide={closeVersions}>
          <Modal.Header closeButton>
            <Modal.Title>{t('mainPage.versionHistory.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {versionsFile && versions === null && <EmptyState>{t('common.loading')}</EmptyState>}
            {versions?.length === 0 && <EmptyState>{t('mainPage.versionHistory.noOtherVersions')}</EmptyState>}
            {versions?.map((version) => (
              <VersionRow
                key={version.version_id}
                onClick={() => {
                  const path = versionsFile as string;
                  closeVersions();
                  openFile(path, version.is_latest ? undefined : version.version_id);
                }}
              >
                <RowIcon icon={faFile}/>
                <RowName>
                  {new Date(version.last_modified).toLocaleString()}
                  {version.is_latest && <VersionBadge>{t('mainPage.versionHistory.current')}</VersionBadge>}
                </RowName>
                <RowMeta>{formatSize(version.size)}</RowMeta>
              </VersionRow>
            ))}
          </Modal.Body>
        </Modal>

        <Modal show={confirmDelete !== null} onHide={cancelDelete}>
          <Modal.Header closeButton>
            <Modal.Title>
              {confirmDelete?.type === 'folder' && t('mainPage.delete.confirmFolderTitle')}
              {confirmDelete?.type === 'file' && t('mainPage.delete.confirmFileTitle')}
              {confirmDelete?.type === 'device' && t('mainPage.delete.confirmDeviceTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {confirmDelete?.type === 'folder' && t('mainPage.delete.confirmFolderMessage', { name: confirmDelete.name })}
            {confirmDelete?.type === 'file' && t('mainPage.delete.confirmFileMessage', { name: confirmDelete.name })}
            {confirmDelete?.type === 'device' && t('mainPage.delete.confirmDeviceMessage', { name: confirmDelete.name })}
          </Modal.Body>
          <Modal.Footer>
            <ModalFooterActions>
              <CrumbLink onClick={cancelDelete}>{t('mainPage.delete.cancel')}</CrumbLink>
              <DangerButton onClick={confirmDeleteAction} disabled={deleting}>
                {deleting ? t('mainPage.delete.deleting') : t('mainPage.delete.confirmButton')}
              </DangerButton>
            </ModalFooterActions>
          </Modal.Footer>
        </Modal>
      </PageWrapper>
    </div>
  );
};

export {
  MainPage,
};
